import moment from "moment";

import { Employee } from "@/Common/Api/Employee/Dto/Employee";
import { Individual } from "@/Common/Api/Individuals/Dto/Individual";
import { ClinicianDTO, LabMessageDTO, MyleActionDTO, MyleNoteDTO, ObservationDTO, RequestDTO } from "@/Common/Api/Labs/Dto/Responses/LabMessageGroupDTO";
import { GetManualLabResultsResponse } from "@/Common/Api/ManualResults/Dtos/GetManualLabResultsResponse";
import { ArrayHelper } from "@/Common/Helpers/ArrayHelper";
import { PatientHelper } from "@/Common/Helpers/PatientHelper";
import { PortalHelper } from "@/Common/Helpers/PortalHelper";
import { ITuple } from "@/Common/Models/ITuple";
import { MyleTabKey } from "@/Common/Models/MyleMenu/enums/MyleTabKey";
import { ResultsCounters } from "@/Common/Models/MyleMenu/Tooltip/ResultsCounters";
import { Patient } from "@/Common/Models/Patient/Patient";
import { PatientTab } from "@/Common/Models/Patient/PatientEnums";
import { Person } from "@/Common/Models/Person/Person";
import { PortalLinkedItemType } from "@/Common/Models/Portal/Enums/PortalLinkedItemType";
import { PortalSyncStatus } from "@/Common/Models/Portal/Enums/PortalSyncStatus";
import { SharedItem } from "@/Common/Models/Portal/SharedItem/SharedItem";
import { SharedItemMessage } from "@/Common/Models/Portal/SharedItem/SharedItemMessage";
import { SharedItemWithLabMetadataLink } from "@/Common/Models/Portal/SharedItem/SharedItemWithLabMetadataLink";
import { RAMQInfo } from "@/Common/Models/RAMQ/RAMQInfo";
import { EmployeeUIModel } from "@/Common/Models/UI/Employee/EmployeeUIModel";
import { LabActionInboxes, LabInboxes, LabTabViews } from "@/Common/Models/UI/Enums";
import { CheckboxTarget } from "@/Common/Models/UI/Inputs/CheckboxTarget";
import * as LocalOperations from "@/Common/Operations/operations.local";
import * as ServerOperations from "@/Common/Operations/operations.server";
import * as SubServerOperations from "@/Common/Operations/operations.server.sub";
import { BaseAction } from "@/Common/Stores/Actions/BaseAction";
import { LocalEvent } from "@/Common/Stores/Actions/LocalEvent";
import { ServerEvent, ServerEventWithRequest, ServerEventWithThen } from "@/Common/Stores/Actions/ServerEvent";
import { BaseServerOperationHandler } from "@/Common/Stores/Handlers/Server/BaseServerOperationHandler";
import { LuxonDateTimeParser } from "@/Common/Utilities/DateAndTime/LuxonDateTimeParser";
import { GetActionsAndNotes } from "@/Labs/Api/Dto/Responses/GetActionsAndNotes";
import { LabActionsInbox } from "@/Labs/Api/Dto/Responses/LabActionsInbox";
import { SearchLabsResponse } from "@/Labs/Api/Dto/Responses/SearchLabsResponse";
import { LabMessage } from "@/Labs/Models/LabMessage";
import { LabMessageGroup } from "@/Labs/Models/LabMessageGroup";
import { LabMessageTableItem } from "@/Labs/Models/LabMessageTableItem";
import { IARLMYLE_Labs_GetInBoxRequest, ILinkPatient } from "@/Labs/Stores/Actions/LabsAction";
import { LabsStore } from "@/Labs/Stores/LabsStore";

class LabsResponseHandler extends BaseServerOperationHandler {
    constructor(action: BaseAction) {
        super(action);

        this.RegisterOperation(ServerOperations.Server.ARLMYLE_Labs_GetInBox, this.GetInBoxDefault, SubServerOperations.Labs.DefaultGetInboxCallbkack);
        this.RegisterOperation(ServerOperations.Server.ARLMYLE_Labs_GetInBox, this.GetInBoxFirst, SubServerOperations.Labs.OpenFirstLabGetInboxCallback);

        this.RegisterOperation(ServerOperations.Server.Myle_ManualLabResults_GetManualLabResults, this.GetManualEntries);

        this.RegisterOperation(ServerOperations.Server.ARLMYLE_TakeAction, this.TakeAction);
        this.RegisterOperation(ServerOperations.Server.ARLMYLE_GetLab, this.GetLab, SubServerOperations.Labs.GetLab_Default);
        this.RegisterOperation(ServerOperations.Server.Myle_LoadPatient, this.LoadPatient);
        this.RegisterOperation(ServerOperations.Server.Myle_ARLMYLE_LinkLabToPatient, this.LinkLabToPatient);

        this.RegisterOperation(ServerOperations.Server.Myle_CreateLabEncounter, this.CreateLabEncounterHold, SubServerOperations.Labs.LabEncounterHold);
        this.RegisterOperation(ServerOperations.Server.Myle_CreateLabEncounter, this.CreateLabEncounterFromPatientHold, SubServerOperations.Labs.LabEncounterHoldFromPatientFile);

        this.RegisterOperation(ServerOperations.Server.Myle_CreateLabEncounter, this.CreateLabEncounterFile, SubServerOperations.Labs.LabEncounterFile);
        this.RegisterOperation(ServerOperations.Server.Myle_CreateLabEncounter, this.CreateLabEncounterFromPatientFile, SubServerOperations.Labs.LabEncounterFileFromPatientFile);

        this.RegisterOperation(ServerOperations.Server.ARLMYLE_GetUnreadCount, this.GetUnreadCount);
        this.RegisterOperation(ServerOperations.Server.Myle_GetActionsCount, this.Myle_GetActionsCount, null, false);
        this.RegisterOperation(ServerOperations.Server.Myle_ManualLabResults_GetNewManualLabResultsCount, this.Myle_GetNewManualLabResultsCount, null, false);

        this.RegisterOperation(ServerOperations.Server.Myle_SaveLabActionsNotes, this.RefreshLabActionsAndNotes);
        this.RegisterOperation(ServerOperations.Server.Myle_Lab_DeleteLabAction, this.RefreshLabActionsAndNotes);
        this.RegisterOperation(ServerOperations.Server.Myle_Lab_ChangeLabActionStatus, this.RefreshLabActionsAndNotes);

        this.RegisterOperation(ServerOperations.Server.Myle_Lab_GetActionsAndNotes, this.Myle_Lab_GetActionsAndNotes);
        this.RegisterOperation(ServerOperations.Server.Myle_Lab_GetLabActionsInbox, this.Myle_Lab_GetLabActionsInbox);

        this.RegisterOperation(ServerOperations.Server.ARLMYLE_GetLab, this.GetLab_FromPortalSyncPopupInInbox, SubServerOperations.Labs.GetLab_FromPortalSyncPopupInInbox);
        this.RegisterOperation(ServerOperations.Server.Myle_Patient_Portal_SyncSharedItem, this.SendLabToPortal);
        this.RegisterOperation(ServerOperations.Server.Myle_Patient_Portal_RevokeSharedItem, this.SendLabToPortal);
        // New Portal Message
        this.RegisterOperation(ServerOperations.Server.Myle_Patient_Portal_GetSharedItem, this.LoadPortalItemStatus, SubServerOperations.PortalLinkedItemStatus.Results);

        this.RegisterOperation(ServerOperations.Server.Myle_LoadAttachedCommunications, this.loadLabAttachedTasks, SubServerOperations.AttachedCommunications.Labs);
        this.RegisterOperation(ServerOperations.Server.RAMQ_CheckSubscription, this.loadRamqInfo);
        this.RegisterOperation(ServerOperations.Server.Myle_CreatePatient, this.createPatientAndLinkToResult);
    }

    private Myle_Lab_GetLabActionsInbox(res: ServerEvent, store: LabsStore): void {
        var currentPrescriptors = store.LabsActionInboxModel.get("PrescriptorClinicians");
        var currentActioners = store.LabsActionInboxModel.get("ActionByClinicians");

        const responseModel: LabActionsInbox = res.Data;
        if (responseModel) {
            store.LabsActionInboxModel = store.LabsActionInboxModel
                .set("TotalQueryRows", responseModel.LabMessageGroupDTO?.TotalMessages ?? 0)
                .set("CurrentPage", responseModel.LabMessageGroupDTO?.CurrentPage ?? 0);

            const actionsInfo = responseModel.MyleActionLookupIds;
            const employeesIdActionByInfo = responseModel.MyleEmployeeIds;

            let allActivePrescriptorsLicences: string[] = [];
            let allActiveEmployeeIdActionBy: string[] = [];

            let tableItems = responseModel.LabMessageGroupDTO?.Messages.map(dto => {
                var labMessage = new LabMessage(dto);
                var item = new LabMessageTableItem(labMessage);

                actionsInfo.forEach((action: ITuple) => {
                    if (action.Item2 == labMessage.Model.LabFillerID) {
                        var actionlookup = cacheStore.GetLookupById(action.Item1);
                        item.StuffedActions += actionlookup.GetValue() + ", ";

                        if (actionlookup.LookupCode === "LabActions10" || actionlookup.LookupCode === "LabActions5") { // Urg
                            item.IsBoldAction = true;
                            item.IsRedAction = true;
                        }

                        if (actionlookup.LookupCode === "LabActions6" || actionlookup.LookupCode === "LabActions11") { // Semi urg
                            item.IsRedAction = true;
                        }

                        if (responseModel.InboxType === LabActionInboxes.Completed) {
                            item.IsCompletedActionInbox = true;
                        }
                    }
                });

                var employeeActionForLab = employeesIdActionByInfo.filter((t: ITuple) => t.Item2 === labMessage.Model.LabFillerID);

                employeeActionForLab.forEach((employeeId: ITuple, idx: number, l: ITuple[]) => {
                    if (idx > 0 && idx < l.length - 1) { item.StuffedEmployees += ", "; }
                    // if (item.StuffedEmployees !== "") { }

                    var employee = cacheStore.GetEmployeeByContactFacetId(employeeId.Item1);

                    if (employee != null) {
                        item.StuffedEmployees += employee.LastName + " " + employee.FirstName;
                        item.ActionByEmployeesContactFacetId.push(employee.ContactFacet_id);
                    }

                    if (allActiveEmployeeIdActionBy.indexOf(employeeId.Item1) < 0) {
                        allActiveEmployeeIdActionBy.push(employeeId.Item1);
                    }
                });

                var prescriber = labMessage.Model.Clinicians.filter((dto: ClinicianDTO) => dto.Type == 4);

                if (prescriber.length > 0) {
                    var id = prescriber[0].ID_Number;
                    if (allActivePrescriptorsLicences.indexOf(id) < 0) {
                        allActivePrescriptorsLicences.push(id);
                    }
                }

                return item;
            });

            var uniquePrescriptorsEmployeeId = allActivePrescriptorsLicences.map(p => {
                var tmpEmployee = cacheStore.GetEmployeeByLicenceFirstOrDefault(p);
                if (tmpEmployee != null)
                    return tmpEmployee.ContactFacet_id;
                else
                    return "";
            }).filter((q: string) => q !== "");

            var prescriptorClinicians = cacheStore.EmployeeCache
                .filter(e => uniquePrescriptorsEmployeeId.indexOf(e.ContactFacet_id) >= 0)
                .map((e: Employee) => {
                    var employee = EmployeeUIModel.fromCompositeObject(e);
                    employee.Target = CheckboxTarget.Myle_Labs_Clinicians;
                    employee.Id;
                    employee.Checked = true;
                    return employee;
                });

            var actionByClinicians = cacheStore.EmployeeCache
                .filter(e => allActiveEmployeeIdActionBy.indexOf(e.ContactFacet_id) >= 0)
                .map((e: Employee) => {
                    var employee = EmployeeUIModel.fromCompositeObject(e);
                    employee.Target = CheckboxTarget.Myle_Labs_Clinicians_List2;
                    employee.Id;
                    employee.Checked = true;
                    return employee;
                });

            var isChecked = function(list: Immutable.List<EmployeeUIModel>, id: string): boolean {
                var l = list.filter(e => e.Id === id);
                if (l.size === 0) return false;
                return l.get(0).Checked;
            };

            prescriptorClinicians.forEach(c => { c.Checked = isChecked(currentPrescriptors, c.Id); });

            actionByClinicians.forEach(c => { c.Checked = isChecked(currentActioners, c.Id); });

            store.LabsActionInboxModel = store.LabsActionInboxModel.set("PrescriptorClinicians", Immutable.List(prescriptorClinicians));

            store.LabsActionInboxModel = store.LabsActionInboxModel.set("ActionByClinicians", Immutable.List(actionByClinicians));

            store.LabsActionInboxModel = store.LabsActionInboxModel.set("ActionsTableDataFull", Immutable.List<LabMessageTableItem>(tableItems));

            labsStore.filterTableItems();
        }
    }

    private Myle_Lab_GetActionsAndNotes(response: ServerEvent, store: LabsStore): void {
        if (response != null && response.Data != null) {
            var responseModel: GetActionsAndNotes = response.Data;

            var labModel = store.LabModel.get("LabMessage");

            if (labModel.Model.LabFillerID === responseModel.requestId) {
                let obxs = labModel.Model.Order.RequestsList
                    .flatMap(obr => obr.ObservationsList ?? []);

                labModel.Model.ParentLevel_MyleNoteDtos = [];
                labModel.Model.Order.RequestsList.forEach((req: RequestDTO) => {
                    req.ObservationsList.forEach((obx: ObservationDTO) => {
                        obx.MyleNotesList = [];
                        obx.MyleActionsList = [];
                    }); // Obx Foreach
                }); // RequestForeach

                // attach remaining orphen actions
                let actions = responseModel.myleActionDTOs;
                if (actions?.length > 0) {
                    for (let currentAction of actions) {
                        let matchingObxs = obxs.filter(obx => obx.Identifier === currentAction.ObservationId);

                        var tmpAction = new MyleActionDTO();
                        tmpAction.Id = currentAction.Id;
                        tmpAction.ActionLookupId = currentAction.ActionLookupId;
                        tmpAction.CreatedByEmployeeId = currentAction.CreatedByEmployeeId;
                        tmpAction.DateCreated = currentAction.DateCreated == null ? null : moment(currentAction.DateCreated).toDate();
                        tmpAction.OriginalValue = currentAction.OriginalValue;
                        tmpAction.CompletedOn = currentAction.CompletedOn == null ? null : moment(currentAction.CompletedOn).toDate();
                        tmpAction.CompletedByEmployeeId = currentAction.CompletedByEmployeeId;

                        if (matchingObxs.length > 0)
                            matchingObxs[0].MyleActionsList.push(tmpAction);
                        else if (obxs.any())
                            obxs[0].MyleActionsList.push(tmpAction);
                    }
                }

                let notes = responseModel.myleNoteDTOs;
                if (notes?.length > 0) {
                    for (let current of notes) {
                        let matchingObxs = obxs.filter(obx => obx.Identifier === current.ObservationId);

                        var tmpNote = new MyleNoteDTO();
                        tmpNote.EmployeeId = current.EmployeeId;
                        tmpNote.TimeStamp = LuxonDateTimeParser.parse(current.TimeStamp).toJSDate();
                        tmpNote.Comment = current.Comment;

                        if (matchingObxs.length > 0)
                            matchingObxs[0].MyleNotesList.push(tmpNote);
                        else
                            labModel.Model.ParentLevel_MyleNoteDtos.push(tmpNote);
                    }
                }

                store.CloseActionNotePopup(store);
                store.LabModel = store.LabModel.delete("LabMessage");
                store.LabModel = store.LabModel.set("LabMessage", labModel);
            }
        }
    }

    private RefreshLabActionsAndNotes(response: ServerEvent, store: LabsStore): void {
        if (response != null && response.Data != null && response.Data.Success) {
            store.CloseActionNotePopup(store);

            if (store.Model.get("CurrentView") === LabTabViews.Lab) {
                store.LabModel.get("LabMessage").Model.Detail = response.Data.Detail;
                store.LabActions.GetLabActionsAndNotes(store.LabModel.get("LabMessage").Model.LabFillerID);
            }
            else
                store.LabActions.RefreshLabs();
        }

        store.LabActions.GetLabsActionsCount();
    }

    /**
     * Changes only emitted if counts change
     * because this code executes often, it forces render to execute even if counts don't change
     * it's best to control manally when to emit changes
     * @param response
     * @param store
     */
    private Myle_GetActionsCount(response: ServerEvent, store: LabsStore): void {
        var tc: number = +response.Data.TotalCount;
        var nc: number = +response.Data.NursesCount;
        var sc: number = +response.Data.SecretariesCount;
        var emitChange = false;
        if (store.Model.get("ActionTotalCount") !== tc) {
            store.Model = store.Model.set("ActionTotalCount", tc);
            emitChange = true;
        }

        if (store.Model.get("ActionNursesCount") !== nc) {
            store.Model = store.Model.set("ActionNursesCount", nc);
            emitChange = true;
        }

        if (store.Model.get("ActionSecretariesCount") !== sc) {
            store.Model = store.Model.set("ActionSecretariesCount", sc);
            emitChange = true;
        }

        if (emitChange === true) {
            store.EmitChangeToListeners();
        }
        else {
            // console.log("skip emit");
        }
    }

    private Myle_GetNewManualLabResultsCount(response: ServerEvent, store: LabsStore): void {
        const totalCount: number = parseInt(response.Data.TotalNewManualLabResultsCount, 10);

        if (store.Model.get("NewManualLabResultsCount") !== totalCount) {
            store.Model = store.Model.set("NewManualLabResultsCount", totalCount);
            store.EmitChangeToListeners();
        }
    }

    private GetUnreadCount(response: ServerEvent): void {
        const resultsCounters = new ResultsCounters(response.Data.UnreadLabsCount);

        cacheStore.UpdateTab(
            MyleTabKey.LabResults,
            response.Data.UnreadLabsCount,
            null,
            null,
            null,
            false,
            { resultsCounters: resultsCounters }
        );
    }

    private GetInBoxDefault(response: ServerEventWithRequest<IARLMYLE_Labs_GetInBoxRequest>, store: LabsStore): void {
        this.GetInboxShared(response, store, false);
    }

    private GetInBoxFirst(response: ServerEventWithRequest<IARLMYLE_Labs_GetInBoxRequest>, store: LabsStore): void {
        this.GetInboxShared(response, store, true /* Open First Last */);
    }

    private GetInboxShared(response: ServerEventWithRequest<IARLMYLE_Labs_GetInBoxRequest>, store: LabsStore, openFirstLast: boolean): void {
        if ((response.requestDto.labCriterias.PatientContactId ?? "") != (store.Model.get("PatientContactId") ?? ""))
            return;

        const responseModel: SearchLabsResponse = response.Data;
        if (responseModel.LabMessageGroupDto == null)
            return;

        store.LabFillerIDsViewedNaviguationPosition = 0;

        const labMessageGroup = new LabMessageGroup();
        labMessageGroup.Model = responseModel.LabMessageGroupDto;

        const tableItems = labMessageGroup.Model.Messages.map(dto => new LabMessageTableItem(new LabMessage(dto)));

        if (openFirstLast) {
            if (tableItems.length > 1) { // Get second lab since the first one is the one currently opened
                store.LabActions.GetLab(tableItems[1].Model.Model.LabFillerID);
            }
            else { // Return to Inbox, no more Labs to go for "Show Next Lab Action"
                store.LabsInboxModel = store.LabsInboxModel.set("CurrentInbox", LabInboxes.New);
                store.Model = store.Model.set("CurrentView", LabTabViews.Inbox);
            }
        }

        store.LabsInboxModel = store.LabsInboxModel
            .set("LabsTableData", Immutable.List<LabMessageTableItem>(tableItems))
            .set("TotalQueryRows", labMessageGroup.Model.TotalMessages)
            .set("CurrentPage", labMessageGroup.Model.CurrentPage)
            .set("SelectAllRowItemsChecked", false);
    }

    private TakeAction(response: ServerEvent, store: LabsStore): void {
        if (response.Data.RefreshInboxAfterAction == true)
            // Refresh Inbox after a classification if it came from InboxView
            store.LabActions.RefreshLabs();
    }

    private GetLab(response: ServerEvent, store: LabsStore): void {
        let labFillerId: string = response.Data.LabFillerID;
        let labMessage: LabMessageDTO = response.Data.LabMessageDTO;

        // if the lab result is invalid, redirect to inbox instead of last opened result
        if (labMessage == null) {
            console.error("The requested lab result doesn't exist.", { labFillerId });
            store.Model = store.Model.set("CurrentView", LabTabViews.Inbox);
            return;
        }

        let getLabResponse = new LabMessage(labMessage);
        let labRelatedPatients: LabRelatedPatient[] = response.Data.labRelatedPatients;

        store.SideNoteModel = Immutable.fromJS({});
        store.labViewScrollPosition = 0;

        store.LabModel = store.LabModel
            .set("IsSaving", false)
            .set("LabMessage", getLabResponse)
            .set("AreAllTasksCompleted", response.Data.AreAllTasksCompleted)
            .set("IndividualTaskLinkedItems", response.Data.IndividualTaskLinkedItems)
            .set("MylePatient", null)
            .set("linkedIndividualIDs", labRelatedPatients && labRelatedPatients.map(x => x.contactId) || []);

        if (!labMessage.IsManualLabEntry) {
            if (labRelatedPatients != null && labRelatedPatients.length > 0)
                store.LabActions.GetPatient(labRelatedPatients.map(x => x.contactId).firstOrDefault());
        }

        store.LabFillerIDsViewedNaviguationPosition = this.RecalculateCurrentOpenedLabPosition(store);
        store.Model = store.Model.set("CurrentView", LabTabViews.Lab);

        if (!labMessage.IsManualLabEntry)
            store.LabActions.GetNewLabsCount();
    }

    private GetManualEntries(response: ServerEvent, store: LabsStore): void {
        const manualLabs: GetManualLabResultsResponse = response.Data;
        let LabsTableData: LabMessageTableItem[] = [];

        let totalMessages: number = 0;

        if (response) {
            totalMessages = manualLabs.TotalCount;
            const labMessages: LabMessageDTO[] = manualLabs.LabMessages ?? [];

            LabsTableData = labMessages.map(dto => {
                const labMessage = new LabMessage(dto);
                return new LabMessageTableItem(labMessage);
            });

            store.LabActions.UpdateNewManualLabResultCount();
        }

        const manualLabSettings = store.getManualLabsInboxFilteringConfiguration();

        store.setManualLabsInboxFilteringConfiguration({ ...manualLabSettings, totalRows: totalMessages });

        store.LabsInboxModel = store.LabsInboxModel
            .set("LabsTableData", Immutable.List<LabMessageTableItem>(LabsTableData));
    }

    private GetLab_FromPortalSyncPopupInInbox(response: ServerEvent, store: LabsStore): void {
        // store labmessage info in model. this allows us to get all lab notes that can be sent as potential portal messages
        let labMessage = new LabMessage(response.Data.LabMessageDTO);
        let labMessageDto = labMessage.Model;

        const isFromPatientLabTab = !IsNullOrWhiteSpace(store.Model.get("PatientContactId"));

        let currentPatientId = patientStore.getCurrentPatientContactId();

        let labRelatedPatients: LabRelatedPatient[] = response.Data.labRelatedPatients;
        let manualLink = labRelatedPatients.firstOrDefault(x => x.isHardLink);

        // ERROR Popup -> Wrong patient
        if (manualLink && manualLink.contactId !== currentPatientId && !IsNullOrWhiteSpace(manualLink.contactId) && isFromPatientLabTab) {
            let currentUser = patientStore.getCurrentPatient();
            let currentUserName = cacheStore.GetString("CurrentUserLabel");
            if (currentUser && currentUser.IndividualResponse != null)
                currentUserName = currentUser.IndividualResponse.FirstName + " " + currentUser.IndividualResponse.LastName;

            let warningMessage = cacheStore.GetString("WarningMsgRelinkLab1Label")
                + `'${manualLink.firstName} ${manualLink.lastName}'`
                + cacheStore.GetString("WarningMsgRelinkLab2Label")
                + `'${currentUserName}'`
                + cacheStore.GetString("WarningMsgRelinkLab3Label");

            sessionStore.showPopup({
                title: cacheStore.GetString("WarningLabel"),
                content: warningMessage
            });
            return;
        }

        // PopupPortalSync Window
        var portalModel = store.LabsInboxModel.get("PortalLinkedItemModel");
        portalModel = portalModel.set("LinkedItemId", labMessageDto.LabFillerID);
        portalModel = portalModel.set("LinkedItemMessages", Immutable.List([]));
        portalModel = portalModel.set("LinkedItemModel", labMessage);
        portalModel = portalModel.set("SharedItem", labMessageDto.SharedItem);
        portalModel = portalModel.set("LinkedItemType", PortalLinkedItemType.Result);

        // now call backend to get linked item status
        if (labMessageDto.SharedItem != null) {
            store.LabActions.LoadPortalSharedItemForLab(labMessageDto.SharedItem.SharedItemId);
        }
        else {
            portalModel = portalModel.set("IsSending", false);

            let portalMessages: SharedItemMessage[] = [];
            let labNotesSent: SharedItemMessage[] = PortalHelper.getLabResultPortalMessages(portalMessages);
            let allLabNotesAsPortalMessages: SharedItemMessage[] = [];
            // parse the lab DTO to find observation notes that can be potentially sent as portal messages
            if (labMessageDto != null) {
                if (labMessageDto.ParentLevel_MyleNoteDtos != null)
                    allLabNotesAsPortalMessages = (PortalHelper.convertLabResultNotesToPortalMessage(labMessageDto.ParentLevel_MyleNoteDtos));
            }
            let labNoteSentIds = labNotesSent.map(x => x.CopiedFromId);
            let labNotesUnSent = allLabNotesAsPortalMessages.filter(x => labNoteSentIds.indexOf(x.CopiedFromId) == -1);

            labNotesUnSent.forEach(x => {
                x.IsSelected = false;
                x.IsDisabled = false;
            });
            // sorting
            labNotesUnSent = ArrayHelper.SortDynamic(labNotesUnSent, "DateCreated", true);
            // build portal snc model
            if (portalModel != null) {
                portalModel = portalModel.set("SendButtonText", cacheStore.GetString("SendLabel"));
                portalModel = portalModel.set("LinkedItemMessages", Immutable.List(labNotesUnSent));
            }
            portalModel = portalModel.set("LinkedItemType", PortalLinkedItemType.Result);
            portalModel = portalModel.set("LinkedItemModel", labMessage);
            portalModel = portalModel.set("SharedItem", labMessageDto.SharedItem);
            portalModel = portalModel.set("DefaultSubject", labMessage.getDefaultSubject());
            portalModel = portalModel.set("ErrorMessage", null);
            store.LabsInboxModel = store.LabsInboxModel.set("IsSendToPatientPortalPopupOpen", true);
        }

        store.LabsInboxModel = store.LabsInboxModel.set("PortalLinkedItemModel", portalModel);
    }

    private LoadPortalItemStatus(response: ServerEvent, store: LabsStore): void {
        let sharedItem: SharedItem = response.Data["SharedItem"];

        let labMessage = store.LabsInboxModel.get("PortalLinkedItemModel").get("LinkedItemModel") as LabMessage;
        let labMessageDto = labMessage.Model;

        let labNotesNotSent: SharedItemMessage[] = PortalHelper.getLabResultPortalMessages(sharedItem.Messages);
        let allLabNotesAsPortalMessages: SharedItemMessage[] = [];

        // parse the lab DTO to find observation notes that can be potentially sent as portal messages
        if (labMessageDto != null) {
            if (labMessageDto.Order != null) {
                if (labMessageDto.Order.RequestsList != null && labMessageDto.Order.RequestsList.length > 0) {
                    labMessageDto.Order.RequestsList.forEach(r => {
                        if (r.ObservationsList != null) {
                            r.ObservationsList.forEach(o => {
                                if (o.MyleNotesList != null) {
                                    let converted = PortalHelper.convertLabResultNotesToPortalMessage(o.MyleNotesList);
                                    if (converted != null)
                                        converted.forEach(c => allLabNotesAsPortalMessages.push(c));
                                }
                            });
                        }
                    });
                }
            }
            if (labMessageDto.ParentLevel_MyleNoteDtos != null) {
                let convertParentNote: SharedItemMessage[] = [];
                convertParentNote = (PortalHelper.convertLabResultNotesToPortalMessage(labMessageDto.ParentLevel_MyleNoteDtos));
                convertParentNote.forEach(c => allLabNotesAsPortalMessages.push(c));
            }
        }

        let labNoteSentIds = labNotesNotSent.map(x => x.CopiedFromId); // document notes portal messages use original document note id as their own ids.
        let labNotesNotUnSent = allLabNotesAsPortalMessages.filter(x => labNoteSentIds.indexOf(x.CopiedFromId) == -1);
        let regularMessagesSent: SharedItemMessage[] = PortalHelper.getPortalMessageOfType(sharedItem.Messages, sharedItem.SharedItemType);

        labNotesNotUnSent.forEach(x => {
            x.IsSelected = false;
            x.IsDisabled = false;
        });
        labNotesNotSent.forEach(x => {
            x.IsSelected = x.Status !== PortalSyncStatus.Revoked;
            x.IsDisabled = true;
        });
        regularMessagesSent.forEach(x => {
            x.IsDisabled = true;
        });

        // sorting
        labNotesNotUnSent = ArrayHelper.SortDynamic(labNotesNotUnSent, "DateCreated");
        let sentMessages = labNotesNotSent.concat(regularMessagesSent);
        sentMessages = ArrayHelper.SortDynamic(sentMessages, "DateCreated");
        let messagesToShow: SharedItemMessage[] = [];
        messagesToShow = messagesToShow.concat(labNotesNotUnSent);
        messagesToShow = messagesToShow.concat(sentMessages);
        messagesToShow = ArrayHelper.SortDynamic(messagesToShow, "DateCreated", true);

        // build portal snc model
        let linkedItemModel = store.LabsInboxModel.get("PortalLinkedItemModel");

        if (linkedItemModel != null) {
            linkedItemModel = linkedItemModel.set("SendButtonText", cacheStore.GetString("SendLabel"));
            linkedItemModel = linkedItemModel.set("LinkedItemMessages", Immutable.List(messagesToShow));
        }

        linkedItemModel = linkedItemModel.set("LinkedItemType", PortalLinkedItemType.Result);
        linkedItemModel = linkedItemModel.set("LinkedItemModel", labMessage);
        linkedItemModel = linkedItemModel.set("SharedItem", labMessageDto.SharedItem);
        linkedItemModel = linkedItemModel.set("DefaultSubject", labMessage.getDefaultSubject());
        linkedItemModel = linkedItemModel.set("ErrorMessage", null);

        store.LabsInboxModel = store.LabsInboxModel.set("PortalLinkedItemModel", linkedItemModel);
        store.LabsInboxModel = store.LabsInboxModel.set("IsSendToPatientPortalPopupOpen", true);
    }

    private RecalculateCurrentOpenedLabPosition(store: LabsStore): number {
        var currentLab = store.LabModel.get("LabMessage");
        if (currentLab != null) {
            var currentLabFillerID = currentLab.Model.LabFillerID;
            var tableData = store.LabsInboxModel.get("LabsTableData");
            if (tableData != null) {
                for (var u = 0; u < tableData.size; u++) {
                    // For index of current labs
                    var tableitem: LabMessageTableItem = tableData.get(u);
                    if (tableitem != null) {
                        if (currentLabFillerID === tableitem.Model.Model.LabFillerID) {
                            return u;
                        }
                    }
                }
            }
        }
        return -1;
    }

    private LoadPatient(response: ServerEvent, store: LabsStore): void {
        var contactId = response.Data.Individual.Contact_id;

        var IsForCurrentLab =
            store.LabModel != null
            && store.LabModel.get("linkedIndividualIDs") != null
            && store.LabModel.get("linkedIndividualIDs").filter((id: string) => id === contactId).length > 0;

        if (IsForCurrentLab) {
            let patient = Patient.FromResponse(response);

            store.LabModel = store.LabModel.delete("MylePatient");
            store.LabModel = store.LabModel.set("MylePatient", patient);

            store.LabActions.loadRamqInfo(patient.IndividualResponse.Contact_id);
        }
    }

    private loadRamqInfo(response: ServerEvent, store: LabsStore): void {
        let data = new RAMQInfo().FromResponseData(response.Data);
        let patient = store.LabModel.get("MylePatient");

        store.LabModel = store.LabModel.set("MylePatient", null);

        if (data)
            patient.setRamq(data);

        store.LabModel = store.LabModel.set("MylePatient", patient);
    }

    private LinkLabToPatient(response: ServerEventWithRequest<ILinkPatient>, store: LabsStore): void {
        if (response.Data.Success != true)
            return;

        let labMessage = store.LabModel.get("LabMessage");
        if (!labMessage || !labMessage.Model)
            return; // CurrentLab is invalid, No lab Data

        if (labMessage.Model.LabFillerID != response.requestDto.labFillerID)
            return; // We're not on the same lab anymore since we started loading;

        let patientContactId = response.requestDto.individualId;

        labMessage.Model.SharedItem = null;
        store.LabModel = store.LabModel
            .set("LabMessage", labMessage)
            .set("linkedIndividualIDs", patientContactId && [patientContactId] || []);

        // Update patient info
        if (patientContactId && patientContactId == store.LabModel.get("SelectedPersonForLinkingLab").ContactId) { // Use current patient
            store.LabModel = store.LabModel.delete("MylePatient");
            store.LabModel = store.LabModel.set("MylePatient", store.LabModel.get("SelectedPersonForLinkingLab").Patient);
        }

        let linkedItemModel = store.LabsInboxModel.get("PortalLinkedItemModel");
        linkedItemModel = linkedItemModel.set("SharedItem", labMessage.Model.SharedItem);
        store.LabsInboxModel = store.LabsInboxModel.set("PortalLinkedItemModel", linkedItemModel);

        if (patientContactId && patientContactId != store.LabModel.get("SelectedPersonForLinkingLab").ContactId) { // Banner is not loaded or still displays old patient, reload it.
            store.LabActions.GetPatient(patientContactId);
        }
        store.LabModel = store.LabModel.set("SelectedPersonForLinkingLab", null);
    }

    private CreateLabEncounterHold(response: ServerEventWithThen<any, () => void>, store: LabsStore): void {
        if (store.saveEncounterLabFillerId != null) {
            if (response.Data.success != null && response.Data.success != false) {
                var operation = new LocalEvent(LocalOperations.LabsUI.HoldLab);
                operation.AddItem("id", store.saveEncounterLabFillerId);
                if (response.Data.OpenEncounterAfterSave) {
                    operation.AddItem("EncounterId", response.Data.EncounterId);
                }
                LocalEventDispatcher.dispatch(operation);
                store.saveEncounterLabFillerId = null;
            }
        }

        if (!response.hasErrors())
            response.then?.();
    }

    private CreateLabEncounterFile(response: ServerEventWithThen<any, () => void>, store: LabsStore): void {
        if (store.saveEncounterLabFillerId != null) {
            if (response.Data.success != null && response.Data.success != false) {
                var operation = new LocalEvent(LocalOperations.LabsUI.FileLab);
                operation.AddItem("id", store.saveEncounterLabFillerId);
                if (response.Data.OpenEncounterAfterSave) {
                    operation.AddItem("EncounterId", response.Data.EncounterId);
                }
                LocalEventDispatcher.dispatch(operation);
                store.saveEncounterLabFillerId = null;
            }
        }

        if (!response.hasErrors())
            response.then?.();
    }

    private CreateLabEncounterFromPatientFile(response: ServerEventWithThen<any, () => void>, store: LabsStore): void {
        if (store.saveEncounterLabFillerId != null) {
            if (response.Data.success != null && response.Data.success != false) {
                // after filing the lab summary to get all his encounters (that will include newly created one)
                // then we switch tab to view that particular encounter.
                const fileOperation = new LocalEvent(LocalOperations.LabsUI.FileLab);
                fileOperation.AddItem("id", store.saveEncounterLabFillerId);
                if (response.Data.OpenEncounterAfterSave) {
                    fileOperation.AddItem("OpenEncounterFromPatientFileContext", true);
                    fileOperation.AddItem("EncounterId", response.Data.EncounterId);
                }

                LocalEventDispatcher.dispatch(fileOperation);
                store.saveEncounterLabFillerId = null;
            }
        }

        if (!response.hasErrors())
            response.then?.();
    }

    private CreateLabEncounterFromPatientHold(response: ServerEventWithThen<any, () => void>, store: LabsStore): void {
        if (store.saveEncounterLabFillerId != null) {
            if (response.Data.success != null && response.Data.success != false) {
                // after filing the lab summary to get all his encounters (that will include newly created one)
                // then we switch tab to view that particular encounter.
                var operation = new LocalEvent(LocalOperations.LabsUI.HoldLab);
                operation.AddItem("id", store.saveEncounterLabFillerId);
                if (response.Data.OpenEncounterAfterSave) {
                    operation.AddItem("OpenEncounterFromPatientFileContext", true);
                    operation.AddItem("EncounterId", response.Data.EncounterId);
                }

                LocalEventDispatcher.dispatch(operation);
                store.saveEncounterLabFillerId = null;
            }
        }

        if (!response.hasErrors())
            response.then?.();
    }

    private SendLabToPortal(response: ServerEvent, store: LabsStore): void {
        let syncModel = store.LabsInboxModel.get("PortalLinkedItemModel");

        if (response.hasErrors()) {
            syncModel = syncModel.set("ErrorMessage", response.Errors[0].Message);
        }
        else {
            let labFIllerId = response.Data["ObjectId"];
            let sharedItem: SharedItemWithLabMetadataLink = response.Data["SharedItem"];
            let sharedItemMessages: SharedItemMessage[] = response.Data["Messages"];
            // Update status for Grid
            let labs = store.LabsInboxModel.get("LabsTableData").toArray();
            labs.forEach(x => {
                if (x.Model.Model.LabFillerID == labFIllerId) {
                    x.Model.Model.SharedItem = sharedItem;
                    x.Model.Model.PortalMessages = sharedItemMessages;
                }
            });
            store.LabsInboxModel = store.LabsInboxModel.set("LabsTableData", labs.toImmutable());

            // Update Status for LabView
            if (store.LabModel != null) {
                let labMessage = store.LabModel.get("LabMessage");
                if (labMessage && labMessage.Model.LabFillerID == labFIllerId) {
                    labMessage.Model.SharedItem = sharedItem;
                    store.LabModel.set("LabMessage", labMessage);
                }
            }

            syncModel = syncModel.set("SharedItem", sharedItem);
            syncModel = syncModel.set("LinkedItemMessages", sharedItemMessages.toImmutable());
            store.LabsInboxModel = store.LabsInboxModel.set("IsSendToPatientPortalPopupOpen", false);

            sessionStore.hideGlobalOverlay();
            $("table.myle-table tbody tr").removeClass("active");
        }

        syncModel = syncModel.set("IsSending", false);
        store.LabsInboxModel = store.LabsInboxModel.set("PortalLinkedItemModel", syncModel);
    }

    private loadLabAttachedTasks(response: ServerEvent, store: LabsStore): void {
        if (response.Data.Success) {
            store.LabModel = store.LabModel
                .set("AreAllTasksCompleted", response.Data.AreAllTasksCompleted)
                .set("IndividualTaskLinkedItems", response.Data.IndividualTaskLinkedItems);
        }
    }

    private createPatientAndLinkToResult(response: ServerEvent, store: LabsStore): void {
        if (response.Errors && response.Errors.length > 0) {
            sessionStore.showPopup(response.Errors);
            return;
        }

        const labFillerID = store.LabModel.get("LabMessage").Model.LabFillerID;
        const individual: Individual = response.Data["Individual"];
        const person = Person.FromIndividual(individual);

        store.LabModel = store.LabModel
            .set("PatientCreationModel", Immutable.fromJS(PatientHelper.getDefaultPatientCreationModel()))
            .set("SelectedPersonForLinkingLab", person)
            .set("ShowCreatePatientPopup", false)
            .set("ShowLinkPatientPopup", false);

        store.LabActions.LinkPatient(labFillerID, individual.Contact_id);

        if (store.LabsInboxModel?.get("IsFromPatient")) {
            patientStore.setActivePatientUI(
                individual.Contact_id,
                true,
                () => {
                    patientStore.PatientUIModels[individual.Contact_id] = patientStore.PatientUIModels[individual.Contact_id].set("ActiveVerticalTab", PatientTab.Results);
                    store.LabActions.GetLab(labFillerID);
                }
            );
        }
        else {
            store.LabActions.GetPatient(individual.Contact_id);
        }
    }
}

class LabRelatedPatient {
    public contactId: string;
    public nam: string;
    public sharedItemId: string;
    public firstName: string;
    public lastName: string;
    public isHardLink: boolean;
}

export { LabsResponseHandler };
